import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiResponse } from '@kinet-open-network/client/core/api-client';
import { mapToModel } from '@kinet-open-network/client/shared/data-access/model';
import {
  downloadBlob,
  getFilenameFromHeaders,
} from '@kinet-open-network/client/shared/util';
import {
  IInvoiceStock,
  Option,
} from '@kinet-open-network/shared/data-access/model';
import { firstValueFrom, map } from 'rxjs';
import { Closure } from '../models/closure.model';
import { Invoice } from '../models/invoice.model';
import { Payment } from '../models/payment.model';

@Injectable({
  providedIn: 'root',
})
export class InvoiceConnectorService {
  protected http = inject(HttpClient);

  createInvoice(id: string) {
    return firstValueFrom(
      this.http
        .post<ApiResponse<Invoice>>(`${this.endpoint}/invoice/${id}`, {})
        .pipe(mapToModel(Invoice, 'data')),
    );
  }

  updateInvoice(id: string) {
    return firstValueFrom(
      this.http
        .put<ApiResponse<Invoice>>(`${this.endpoint}/invoice/${id}`, {})
        .pipe(mapToModel(Invoice, 'data')),
    );
  }

  deleteInvoice(id: string) {
    return firstValueFrom(
      this.http.delete<ApiResponse<Invoice>>(
        `${this.endpoint}/invoice/${id}`,
        {},
      ),
    );
  }

  createPayment(id: string) {
    return firstValueFrom(
      this.http
        .post<ApiResponse<Payment>>(`${this.endpoint}/payment/${id}`, {})
        .pipe(mapToModel(Payment, 'data')),
    );
  }

  createClosure(id: string) {
    return firstValueFrom(
      this.http
        .post<ApiResponse<Closure>>(`${this.endpoint}/closure/${id}`, {})
        .pipe(mapToModel(Closure, 'data')),
    );
  }

  async exportClosure(id: string): Promise<void> {
    const response = await firstValueFrom(
      this.http.get(`${this.endpoint}/closure/${id}/export`, {
        responseType: 'blob',
        observe: 'response',
      }),
    );
    const blob = response.body as Blob;
    const filename = getFilenameFromHeaders(response.headers);
    return downloadBlob(blob, filename ?? 'uzavierka');
  }

  health() {
    return this.http
      .get<ApiResponse<any>>(`${this.endpoint}/health`)
      .pipe(map((res) => res.data));
  }

  getAccountingOptions() {
    return this.http
      .get<
        ApiResponse<
          { id: string; name: string; debit: string; credit: string }[]
        >
      >(`${this.endpoint}/accounting`)
      .pipe(
        map((res) =>
          res.data?.map(
            (item) =>
              ({
                value: item.id,
                label: item.name,
                debit: item.debit,
                credit: item.credit,
              }) as Option & {
                debit: string;
                credit: string;
              },
          ),
        ),
      );
  }

  getStocks() {
    return this.http
      .get<ApiResponse<IInvoiceStock[]>>(`${this.endpoint}/stocks`)
      .pipe(map((res) => res.data));
  }

  createStock(data: Partial<IInvoiceStock>) {
    return this.http.post<ApiResponse<string>>(`${this.endpoint}/stocks`, data);
  }

  get endpoint(): string {
    return '/invoicing/connector';
  }
}
